import React, { useState } from 'react';
import { BusinessIdea } from '../../services/businessIdeaTypes';

interface BusinessPlanWizardProps {
  businessIdea: BusinessIdea;
  onSubmit: (additionalData: BusinessPlanAdditionalData) => Promise<void>;
  onCancel: () => void;
  isOpen?: boolean;
}

export interface BusinessPlanAdditionalData {
  founderName: string;
  businessName: string;
  startDate: string;
  location: {
    city: string;
    state: string;
    country: string;
  };
  targetMarket: string;
  funding: {
    amount: string;
    source: string;
    currency: string;
  };
}

const initialData: BusinessPlanAdditionalData = {
  founderName: '',
  businessName: '',
  startDate: '',
  location: {
    city: '',
    state: '',
    country: ''
  },
  targetMarket: '',
  funding: {
    amount: '',
    source: '',
    currency: 'USD'
  }
};

const BusinessPlanWizard: React.FC<BusinessPlanWizardProps> = ({ businessIdea, onSubmit, onCancel, isOpen = true }) => {
  const [step, setStep] = useState<number>(1);
  const [data, setData] = useState<BusinessPlanAdditionalData>(initialData);
  
  const totalSteps = 4;
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Handle nested properties
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setData(prev => {
        if (parent === 'location') {
          return {
            ...prev,
            location: {
              ...prev.location,
              [child]: value
            }
          };
        } else if (parent === 'funding') {
          return {
            ...prev,
            funding: {
              ...prev.funding,
              [child]: value
            }
          };
        }
        return prev; // Return unchanged if parent is not recognized
      });
    } else {
      setData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  const nextStep = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      onSubmit(data);
    }
  };
  
  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1);
    } else {
      onCancel();
    }
  };
  
  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Business Information</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700">Business Name</label>
              <input
                type="text"
                name="businessName"
                value={data.businessName}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Enter business name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Founder Name</label>
              <input
                type="text"
                name="founderName"
                value={data.founderName}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Enter founder name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Planned Start Date</label>
              <input
                type="date"
                name="startDate"
                value={data.startDate}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Business Location</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700">City</label>
              <input
                type="text"
                name="location.city"
                value={data.location.city}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Enter city"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">State/Province</label>
              <input
                type="text"
                name="location.state"
                value={data.location.state}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Enter state or province"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Country</label>
              <input
                type="text"
                name="location.country"
                value={data.location.country}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Enter country"
              />
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Target Market</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700">Describe your target market</label>
              <textarea
                name="targetMarket"
                value={data.targetMarket}
                onChange={handleChange}
                rows={4}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Describe your target customers, demographics, needs, etc."
              />
            </div>
          </div>
        );
      case 4:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Funding Information</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700">Expected Funding Amount</label>
              <div className="mt-1 flex rounded-md shadow-sm">
                <div className="relative flex items-stretch flex-grow focus-within:z-10">
                  <input
                    type="text"
                    name="funding.amount"
                    value={data.funding.amount}
                    onChange={handleChange}
                    className="block w-full rounded-none rounded-l-md border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    placeholder="e.g. 50000"
                  />
                </div>
                <select
                  name="funding.currency"
                  value={data.funding.currency || 'USD'}
                  onChange={handleChange}
                  className="-ml-px relative inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 text-sm font-medium rounded-r-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                  <option value="GBP">GBP (£)</option>
                  <option value="JPY">JPY (¥)</option>
                  <option value="CAD">CAD (C$)</option>
                  <option value="AUD">AUD (A$)</option>
                  <option value="INR">INR (₹)</option>
                  <option value="CNY">CNY (¥)</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Funding Source</label>
              <input
                type="text"
                name="funding.source"
                value={data.funding.source}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="e.g. Personal savings, Angel investors, Bank loan"
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
        <button
          onClick={onCancel}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
          type="button"
        >
          <span className="sr-only">Close</span>
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900">Business Plan Wizard</h2>
          <p className="text-gray-600 mt-1">Step {step} of {totalSteps}: Provide additional information for your business plan</p>
        </div>
        
        <div className="mb-8">
          {renderStep()}
        </div>
        
        <div className="flex justify-between">
          <button
            onClick={prevStep}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded"
          >
            {step === 1 ? 'Cancel' : 'Back'}
          </button>
          <button
            onClick={nextStep}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded"
          >
            {step === totalSteps ? 'Complete' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BusinessPlanWizard;
